import { FC, useMemo, ReactNode } from 'react';
import MoreMenu from '@/components/more-menu';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { ConnectorType, getTypedConnector } from '@/lib/models/connector';
import { AwsConfig } from '@/lib/models/connector/aws-connector.model';
import { ProxyConfig } from '@/lib/models/connector/proxy-connector.model';
import { CloudflareConfig } from '@/lib/models/connector/cloudflare-connector.model';
import { DohConfig } from '@/lib/models/connector/doh-connector.model';
import CopyMenuItem from '@/components/more-menu/copy-menu-item';
import { UpdateMenuItemProps } from '@/components/more-menu/helpers';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';

interface Props extends UpdateMenuItemProps {
  connector: ConnectorModel | null;
  connectorType: ConnectorType | '';
  isNew: boolean;
  canViewConnectorArn: boolean;
}

const ConnectorMenu: FC<Props> = ({
  connector,
  connectorType,
  isNew,
  canViewConnectorArn,
  showUpdateMenu,
  ...rest
}) => {
  const menuItems = useMemo(() => {
    if (isNew || !connector) {
      return [];
    }

    const typedConnectorData = getTypedConnector(connector);
    const items: ReactNode[] = [];

    const { config } = typedConnectorData;

    // AWS connector menu items
    if (connectorType === 'aws') {
      const { crossAccountRoleARN, externalId } = config as AwsConfig;
      if (canViewConnectorArn && crossAccountRoleARN) {
        items.push(
          <CopyMenuItem
            label="Copy Cross Account Role ARN"
            icon="copy-id"
            value={crossAccountRoleARN}
            key="copy-cross-account-role-arn"
          />
        );
      }
      if (canViewConnectorArn && externalId) {
        items.push(
          <CopyMenuItem
            value={externalId}
            label="Copy External ID"
            icon="copy-id"
            key="copy-external-id"
          />
        );
      }
    }

    // Proxy connector menu items
    if (connectorType === 'proxy') {
      const { pacFileUrl, serviceUrl } = config as ProxyConfig;
      items.push(
        <CopyMenuItem
          value={serviceUrl ?? pacFileUrl}
          label="Copy PAC File URL"
          icon="link"
          key="copy-pac-file-url"
        />
      );
    }

    // Cloudflare connector menu items
    if (connectorType === 'cloudflare') {
      const { connectorId } = config as CloudflareConfig;
      items.push(
        <CopyMenuItem
          value={connectorId}
          label="Copy Connector ID"
          icon="copy-id"
          key="copy-connector-id"
        />
      );
    }

    // DoH connector menu items
    if (connectorType === 'doh') {
      const { serviceUrl } = config as DohConfig;
      if (serviceUrl) {
        items.push(
          <CopyMenuItem
            value={serviceUrl}
            label="Copy DoH URL"
            icon="link"
            key="copy-doh-url"
          />
        );
      }
    }

    // Catalog URL applies to every ingress connector type that exposes one
    const { catalogUrl } = config as { catalogUrl?: string };
    if (catalogUrl) {
      items.push(
        <CopyMenuItem
          value={catalogUrl}
          label="Copy Catalog URL"
          icon="link"
          key="copy-catalog-url"
        />
      );
    }

    if (showUpdateMenu) {
      items.push(<UpdateMenuItems {...rest} key="update-menu-items" />);
    }

    return items;
  }, [isNew, connector, connectorType, showUpdateMenu, canViewConnectorArn, rest]);

  return <MoreMenu menuId="connector-menu">{menuItems}</MoreMenu>;
};

export default ConnectorMenu;
